import { WalletAddress } from '../Ether';
import { GemType } from './Game';

export enum ChatRoomType {
  GemCreator = 'gem_creator',
}

export enum ChatMessageType {
  Text = 'text',
}

export interface ChatRoomSchema {
  id: number;
  type: string;
  creator_id: number;
  created_at: string;
  updated_at: string;
}

export interface InsertChatRoomSchema {
  type: string;
  creator_id: number;
  created_at?: string;
  updated_at?: string;
}

export interface ChatMessageSchema {
  id: number;
  type: string;
  room_id: number;
  sender_id: number;
  created_at: string;
  updated_at: string;
  text: string;
  in_reply_to?: number;
}

export interface InsertChatMessageSchema {
  type: string;
  room_id: number;
  sender_id: number;
  text: string;
  in_reply_to?: number;
  created_at?: string;
  updated_at?: string;
}

export interface ChatRoomSenderSchema {
  sender_id: number;
  sender_username: string;
  sender_wallet_address: string;
  sender_twitter_handle?: string;
  sender_twitter_displayname?: string;
  sender_twitter_picture?: string;
}

export interface ChatRoomCreatorSchema {
  creator_id: number;
  creator_username: string;
  creator_wallet_address: string;
  creator_twitter_handle?: string;
  creator_twitter_displayname?: string;
  creator_twitter_picture?: string;
}

export interface ChatRoomItemHoldingSchema {
  offchain_game_id: number;
  source: string;
  price: string;
  gem_type: GemType;
}

export interface ChatReadReceiptSchema {
  room_id: number;
  user_id: number;
  message_id: number;
  created_at: string;
  updated_at: string;
}

export interface InsertChatReadReceiptSchema {
  room_id: number;
  user_id: number;
  message_id: number;
  created_at?: string;
  updated_at?: string;
}

export interface ChatPresenceSchema {
  user_id: number;
  created_at: string;
  updated_at: string;
}

export interface InsertChatPresenceSchema {
  user_id: number;
  created_at?: string;
  updated_at?: string;
}

export interface ChatRoomUserData {
  id: number;
  username: string;
  walletAddress: WalletAddress;
  twitter?: {
    handle?: string;
    displayName?: string;
    picture?: string;
  };
}

export interface ChatRoomData {
  id: number;
  type: string;
  creatorId: number;
  creator?: ChatRoomUserData;
  createdAt: string;
  updatedAt: string;
}

export interface InsertChatRoomData {
  type: string;
  creatorId: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ChatMessageData {
  id: number;
  type: string;
  roomId: number;
  senderId: number;
  sender?: ChatRoomUserData;
  createdAt: string;
  updatedAt: string;
  text: string;
  inReplyTo?: number;
}

export interface InsertChatMessageData {
  type: string;
  roomId: number;
  senderId: number;
  createdAt?: string;
  updatedAt?: string;
  text: string;
  inReplyTo?: number;
}

export interface ChatRoomItemHoldingData {
  offChainGameId: number;
  source: string;
  price: number;
}

export interface ChatRoomStatsData {
  memberCount?: number;
  onlineCount?: number;
  gameItemHoldingCount?: number;
  gameItemHoldingValue?: string;
  gameItemTotalCount?: number;
}

export interface ChatReadReceiptData {
  roomId: number;
  userId: number;
  messageId: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChatPresenceData {
  userId: number;
  createdAt: string;
  updatedAt: string;
}

export const getUserDataFromCreatorSchema = (
  schema: ChatRoomCreatorSchema,
): ChatRoomUserData => ({
  id: Number(schema.creator_id),
  username: schema.creator_username,
  walletAddress: schema.creator_wallet_address as WalletAddress,
  twitter: {
    handle: schema.creator_twitter_handle,
    displayName: schema.creator_twitter_displayname,
    picture: schema.creator_twitter_picture,
  },
});

export const getUserDataFromSenderSchema = (
  schema: ChatRoomSenderSchema,
): ChatRoomUserData => ({
  id: Number(schema.sender_id),
  username: schema.sender_username,
  walletAddress: schema.sender_wallet_address as WalletAddress,
  twitter: {
    handle: schema.sender_twitter_handle,
    displayName: schema.sender_twitter_displayname,
    picture: schema.sender_twitter_picture,
  },
});

export const getChatRoomDataFromSchema = (
  schema: ChatRoomSchema,
  extra?: {
    creatorSchema?: ChatRoomCreatorSchema;
    creatorData?: ChatRoomUserData;
  },
): ChatRoomData => ({
  id: Number(schema.id),
  type: schema.type,
  creatorId: Number(schema.creator_id),
  creator: extra?.creatorData ?? (extra?.creatorSchema ? getUserDataFromCreatorSchema(extra.creatorSchema) : undefined),
  createdAt: schema.created_at,
  updatedAt: schema.updated_at,
});

export const getChatMessageDataFromSchema = (
  schema: ChatMessageSchema,
  extra?: {
    senderSchema?: ChatRoomSenderSchema;
    senderData?: ChatRoomUserData;
  },
): ChatMessageData => ({
  id: Number(schema.id),
  type: schema.type,
  createdAt: schema.created_at,
  updatedAt: schema.updated_at,
  roomId: Number(schema.room_id),
  senderId: Number(schema.sender_id),
  sender: extra?.senderData ?? (extra?.senderSchema ? getUserDataFromSenderSchema(extra.senderSchema) : undefined),
  text: schema.text,
  inReplyTo: Number(schema.in_reply_to),
});

export const getInsertChatMessageSchemaFromData = (
  data: InsertChatMessageData,
): InsertChatMessageSchema => ({
  type: data.type,
  room_id: data.roomId,
  sender_id: data.senderId,
  text: data.text,
  created_at: data.createdAt,
  updated_at: data.updatedAt,
  in_reply_to: data.inReplyTo,
});

export const getInsertChatRoomSchemaFromData = (
  data: InsertChatRoomData,
): InsertChatRoomSchema => ({
  type: data.type,
  creator_id: data.creatorId,
  created_at: data.createdAt,
  updated_at: data.updatedAt,
});

export const getChatReadReceiptDataFromSchema = (
  schema: ChatReadReceiptSchema,
): ChatReadReceiptData => ({
  roomId: Number(schema.room_id),
  userId: Number(schema.user_id),
  messageId: Number(schema.message_id),
  createdAt: schema.created_at,
  updatedAt: schema.updated_at,
});

export const getChatReadReceiptSchemaFromData = (
  data: ChatReadReceiptData,
): ChatReadReceiptSchema => ({
  room_id: data.roomId,
  user_id: data.userId,
  message_id: data.messageId,
  created_at: data.createdAt,
  updated_at: data.updatedAt,
});

export const getChatPresenceDataFromSchema = (
  schema: ChatPresenceSchema,
): ChatPresenceData => ({
  userId: Number(schema.user_id),
  createdAt: schema.created_at,
  updatedAt: schema.updated_at,
});
